'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { ScrollText } from 'lucide-react';

export default function TermsAndConditionsPage() {
  return (
    <AppLayout activeRoute="/terms-and-conditions">
      <div className="max-w-3xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <ScrollText size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Terms &amp; Conditions</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Effective Date: 21 May 2026</p>
          </div>
        </div>

        <div className="card-base p-8 space-y-8 text-sm text-muted-foreground leading-relaxed">
          <p className="text-foreground/80">
            These Terms &amp; Conditions govern your access to and use of PropDataX.
          </p>
          <p className="text-foreground/80">
            By using the website or services, you agree to these terms.
          </p>

          <Section title="1. Use of Services">
            <p>Users agree to use PropDataX only for lawful purposes and in accordance with applicable laws and regulations.</p>
            <p className="mt-3">Users must not:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Attempt unauthorized access to systems or accounts</li>
              <li>Distribute malicious software or harmful code</li>
              <li>Use the platform for fraudulent or illegal activities</li>
              <li>Copy, resell, or misuse platform content without permission</li>
            </ul>
          </Section>

          <Section title="2. Account Registration">
            <p>Users may be required to create an account to access certain features.</p>
            <p className="mt-3">Users are responsible for:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Maintaining confidentiality of login credentials</li>
              <li>All activities conducted under their accounts</li>
              <li>Providing accurate and updated information</li>
            </ul>
          </Section>

          <Section title="3. Intellectual Property">
            <p>
              All platform content, branding, software, design elements, logos, and materials are owned by or licensed to PropDataX.
            </p>
            <p className="mt-3">Unauthorized reproduction or distribution is prohibited.</p>
          </Section>

          <Section title="4. Payments and Billing">
            <p>Certain features may require paid subscriptions or one-time payments.</p>
            <p className="mt-3">
              Users agree to provide valid payment information and authorize payment processing through third-party payment providers.
            </p>
          </Section>

          <Section title="5. Service Availability">
            <p>We strive to maintain uninterrupted service but do not guarantee continuous availability.</p>
            <p className="mt-3">We may suspend or modify services for maintenance, upgrades, or operational reasons.</p>
          </Section>

          <Section title="6. Limitation of Liability">
            <p>PropDataX shall not be liable for:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Indirect or consequential damages</li>
              <li>Data loss or business interruption</li>
              <li>Delays or service disruptions</li>
              <li>Third-party platform failures</li>
            </ul>
            <p className="mt-3">Users access the platform at their own risk.</p>
          </Section>

          <Section title="7. Termination">
            <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
          </Section>

          <Section title="8. Governing Law">
            <p>
              These Terms shall be governed by and interpreted in accordance with the laws of India.
            </p>
            <p className="mt-3">
              Any disputes shall be subject to the jurisdiction of courts located in Maharashtra, India.
            </p>
          </Section>

          <Section title="9. Changes to Terms">
            <p>
              We may update these Terms periodically. Continued use of the platform after updates constitutes acceptance of revised terms.
            </p>
          </Section>

          <Section title="10. Contact Information">
            <div className="mt-3 p-4 bg-muted/40 rounded-lg border border-border space-y-1">
              <p className="font-semibold text-foreground">PropDataX</p>
              <p>Email: <a href="mailto:sales@propdatax.com" className="text-amber-400 hover:text-amber-300 transition-colors">sales@propdatax.com</a></p>
              <p>Website: <a href="https://propdatax.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">https://propdatax.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
